document.addEventListener('DOMContentLoaded', () => {

  // EXAMPLE
  // <div class="flex flex-row wrap <?=implode(' ', $classes); ?>"><?php
  //     foreach( $stats as $index => $stat ) :
  //       $animation_name = 'stagger-'.$index;
  //       $label = save_get_field($stat, 'label', '');
  //       $value = save_get_field($stat, 'value', ''); ?>
  //       <div data-animate-trigger="<?=$animation_name ;?>"
  //         data-animate-group="staggergroup"
  //         data-animate-once="false"
  //         data-group-stagger="75"
  //         data-group-staggertype="group.element"
  //         class="w-full">
  //         <span class="inline-block font-bold text-2xl translate-y-[50px] transition-all duration-700" 
  //           data-animate-name="<?=$animation_name ;?>"
  //           data-animate-classes="translate-y-0">
  //           <?=$value;?>
  //         </span><br>
  //         <span class="inline-block translate-y-[50px] transition-all duration-700" 
  //           data-animate-name="<?=$animation_name ;?>"
  //           data-animate-classes="translate-y-0">
  //           <?=$label;?>
  //         </span><br>
  //       </div><?php
  //     endforeach; ?>
  // </div>

  // ===== Global defaults ==================================================
  const DEFAULTS = {
    once: false,             // data-animate-once
    stagger: 0,              // data-group-stagger (ms)
    staggerType: 'element',  // 'group' | 'element' | 'group.element'
  };

  // ===== Utils ============================================================
  const getClassPrefix = (cls) => {
    const parts = cls.split('-');
    return parts.length >= 2 ? parts.slice(0, -1).join('-') + '-' : null;
  };

  const applyWithPrefixReplace = (el, classes) => {
    classes.forEach((cls) => {
      const prefix = getClassPrefix(cls);
      if (prefix) {
        [...el.classList].forEach((ec) => {
          if (ec.startsWith(prefix)) el.classList.remove(ec);
        });
      }
      el.classList.add(cls);
    });
  };

  const uniqueNodes = (nodes) => {
    const seen = new Set();
    const out = [];
    nodes.forEach((n) => { if (!seen.has(n)) { seen.add(n); out.push(n); } });
    return out;
  };

  const rememberOriginalIfNeeded = (el) => {
    if (!el.dataset._origClasses) {
      el.dataset._origClasses = JSON.stringify([...el.classList]);
    }
  };

  const applyAnimationToTargets = (targets) => {
    targets.forEach((el) => {
      rememberOriginalIfNeeded(el);
      const clsAttr = el.getAttribute('data-animate-classes') || '';
      const classes = clsAttr.split(/\s+/).filter(Boolean);
      if (!classes.length) return;

      const before = new Set(el.classList);
      applyWithPrefixReplace(el, classes);
      const after = new Set(el.classList);

      const applied = [...after].filter((c) => !before.has(c));
      el.dataset._appliedClasses = JSON.stringify(applied);
    });
  };

  const resetTargetsToOriginal = (targets) => {
    targets.forEach((el) => {
      const applied = JSON.parse(el.dataset._appliedClasses || '[]');
      if (applied.length) el.classList.remove(...applied);

      const orig = JSON.parse(el.dataset._origClasses || '[]');
      el.className = '';
      if (orig.length) el.classList.add(...orig);

      el.dataset._appliedClasses = '';
      // keep _origClasses for future enters
    });
  };

  // ===== Triggers, groups, and meta ======================================
  const triggers = [...document.querySelectorAll('[data-animate-trigger]')];

  // Group triggers by data-animate-group (null -> independent groups per trigger)
  const groups = new Map();                           // key -> Set<trigger>
  const groupMeta = new Map();                        // key -> { visibleCount, animatedOnce }

  // Keep insertion order = DOM order
  triggers.forEach((tr, idx) => {
    const groupAttr = tr.getAttribute('data-animate-group');
    const key = groupAttr ?? `__solo_${idx}`;         // unique key for ungrouped trigger
    if (!groups.has(key)) groups.set(key, new Set());
    groups.get(key).add(tr);
    if (!groupMeta.has(key)) groupMeta.set(key, { visibleCount: 0, animatedOnce: false });
  });

  const getBatchForKey = (key) => [...(groups.get(key) || [])];

  const getStaggerFromBatch = (batch) => {
    for (const tr of batch) {
      const s = tr.getAttribute('data-group-stagger');
      if (s != null && s !== '') return parseInt(s, 10) || 0;
    }
    return DEFAULTS.stagger;
  };

  const getStaggerTypeFromBatch = (batch) => {
    for (const tr of batch) {
      const type = tr.getAttribute('data-group-staggertype');
      if (type) return type;
    }
    return DEFAULTS.staggerType;
  };

  const getOnceFromBatch = (batch) => {
    for (const tr of batch) {
      const v = tr.getAttribute('data-animate-once');
      if (v != null) return String(v).toLowerCase() === 'true';
    }
    return DEFAULTS.once;
  };

  const getGroupsTargets = (batch) => {
    // Return an array where each entry corresponds to one "inner group":
    // i.e., targets for each trigger in the batch, in DOM order.
    const lists = batch.map((tr) => {
      const name = tr.getAttribute('data-animate-trigger') || '';
      if (!name) return [];
      const found = document.querySelectorAll(`[data-animate-name="${CSS.escape(name)}"]`);
      return uniqueNodes([...found]);
    });
    return lists;
  };

  // Schedule helper supporting: group, element, group.element
  const schedule = (groupsTargets, staggerType, stagger, fnPerTargets) => {
    if (stagger <= 0) {
      if (staggerType === 'group') {
        groupsTargets.forEach(targets => fnPerTargets(targets));
      } else if (staggerType === 'element' || staggerType === 'group.element') {
        groupsTargets.forEach(targets => targets.forEach(el => fnPerTargets([el])));
      }
      return;
    }

    if (staggerType === 'group') {
      groupsTargets.forEach((targets, gi) => {
        setTimeout(() => fnPerTargets(targets), gi * stagger);
      });
    } else if (staggerType === 'element') {
      groupsTargets.forEach((targets) => {
        targets.forEach((el, i) => {
          setTimeout(() => fnPerTargets([el]), i * stagger);
        });
      });
    } else if (staggerType === 'group.element') {
      // two-tier compound delay: groupIndex * stagger + elementIndex * stagger
      groupsTargets.forEach((targets, gi) => {
        targets.forEach((el, i) => {
          const delay = gi * stagger + i * stagger;
          setTimeout(() => fnPerTargets([el]), delay);
        });
      });
    }
  };

  // ===== Observer with per-group coordination ============================
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const tr = entry.target;
      const groupAttr = tr.getAttribute('data-animate-group');
      const key = groupAttr ?? `__solo_${tr.dataset.__soloIndex}`;

      // If this is an ungrouped trigger, ensure it has a stable key
      if (!groupAttr && !groupMeta.has(key)) {
        // Shouldn't happen because we created these above, but guard anyway
        groupMeta.set(key, { visibleCount: 0, animatedOnce: false });
        groups.set(key, new Set([tr]));
      }

      const meta = groupMeta.get(key);
      const batch = getBatchForKey(key);

      // Compute once/stagger settings for the whole batch
      const once = getOnceFromBatch(batch);
      const stagger = getStaggerFromBatch(batch);
      const staggerType = getStaggerTypeFromBatch(batch);

      if (entry.isIntersecting) {
        // First trigger entering the viewport for this group?
        meta.visibleCount += 1;

        if (once && meta.animatedOnce) return;

        if (meta.visibleCount === 1) {
          // Schedule ENTER for the whole group exactly once
          const groupsTargets = getGroupsTargets(batch);
          schedule(groupsTargets, staggerType, stagger, (targets) => applyAnimationToTargets(targets));
          if (once) meta.animatedOnce = true;
        }
      } else {
        // A trigger in the group left the viewport
        meta.visibleCount = Math.max(0, meta.visibleCount - 1);

        // When the last one leaves, reset the whole group (if not once)
        if (!once && meta.visibleCount === 0) {
          const groupsTargets = getGroupsTargets(batch);
          schedule(groupsTargets, staggerType, stagger, (targets) => resetTargetsToOriginal(targets));
        }
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -100px 0px' });

  // Attach a stable solo index for ungrouped triggers
  let soloCounter = 0;
  triggers.forEach((tr) => {
    if (!tr.hasAttribute('data-animate-group')) {
      tr.dataset.__soloIndex = String(soloCounter++);
    }
    observer.observe(tr);
  });
});